import React from 'react';
import { Box, Typography } from '@mui/material';
import { UserSubscriptionContextData } from '../../../../contexts/UserContext';
import { format } from 'date-fns';

type SubscriptionNotificationProps = {
  currentSubscription: UserSubscriptionContextData;
};

// Arena palette — mirrors the dashboard cards / the Subscription hero so the
// status block reads as part of the same system (was a flat MUI `divider` box).
const C = {
  cyan: '#38e8ff',
  gold: '#ffcf40',
  green: '#42e6a4',
  text: '#e8f4fb',
  dim: '#8ea3b3',
  dimmer: '#5c6b78',
  mono: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace'
};

// Shared arena panel shell — dark gradient, cyan hairline, soft corner glow.
// Page margins deliberately live on the caller, not here: on wide screens this
// panel sits in a grid cell beside the giveaway timers, where its own `mx`/`mb`
// would push it out of alignment with the column next to it. `height: 100%` lets
// it match that column's height instead of floating at its content height.
const panelSx = {
  position: 'relative',
  overflow: 'hidden',
  height: '100%',
  // Beside the taller timers column this panel is stretched well past its content,
  // so centre what is in it rather than leaving it stranded against the top edge.
  // On the stacked layout the row is content-height and this is a no-op.
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  // shrink-to-fit rather than the flex default of stretch: the status pill is an
  // inline-flex chip and would otherwise be pulled out to the full panel width
  alignItems: 'center',
  px: { xs: 2.5, md: 4 },
  py: { xs: 3, md: 4 },
  borderRadius: '16px',
  border: '1px solid rgba(56, 232, 255, 0.22)',
  background:
    'linear-gradient(180deg, rgba(11, 17, 25, 0.74), rgba(7, 11, 17, 0.78))',
  boxShadow: '0 18px 40px -26px rgba(0, 0, 0, 0.9)',
  textAlign: 'center',
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: '0 auto auto 0',
    width: 320,
    height: 180,
    pointerEvents: 'none',
    background:
      'radial-gradient(420px 220px at 0 0, rgba(56, 232, 255, 0.08), transparent 65%)'
  }
} as const;

const pillSx = (color: string) =>
  ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.75,
    px: 1.25,
    py: 0.5,
    borderRadius: '999px',
    border: `1px solid ${color}55`,
    font: `800 10px ${C.mono}`,
    letterSpacing: '0.18em',
    textTransform: 'uppercase',
    color
  }) as const;

// lg+ is the two-column layout, where this panel has height to spare — the type
// steps up there so it fills the column instead of floating in it.
const headingSx = {
  mt: 1.5,
  fontWeight: 900,
  fontSize: { xs: 'clamp(24px, 3.4vw, 36px)', lg: 44 },
  lineHeight: 1.05,
  letterSpacing: '-0.02em',
  color: C.text
} as const;

const bodySx = {
  mt: { xs: 1.5, lg: 2.5 },
  mx: 'auto',
  maxWidth: { xs: 560, lg: 620 },
  fontFamily: C.mono,
  fontWeight: 600,
  fontSize: { xs: 13, lg: 15 },
  lineHeight: 1.6,
  color: C.dim
} as const;

const eyebrowSx = {
  mt: 0.5,
  font: `800 11px ${C.mono}`,
  letterSpacing: '0.34em',
  textTransform: 'uppercase',
  color: C.gold
} as const;

// The plan artwork already carries the tier/price/benefits — frame it like a
// premium card (cyan-lit border + inner glow + float) instead of a raw image.
const imgFrameSx = {
  mt: { xs: 2.5, lg: 3.5 },
  mb: 0.5,
  mx: 'auto',
  width: 'fit-content',
  maxWidth: '100%',
  p: { xs: 1.5, md: 2, lg: 2.5 },
  borderRadius: '20px',
  border: '1px solid rgba(56, 232, 255, 0.30)',
  background:
    'radial-gradient(130% 100% at 50% 0%, rgba(56, 232, 255, 0.12), rgba(7, 11, 17, 0.35) 72%)',
  boxShadow:
    'inset 0 0 44px rgba(56, 232, 255, 0.07), 0 24px 55px -32px rgba(0, 0, 0, 0.9)',
  animation: 'subFloat 5.5s ease-in-out infinite',
  '@keyframes subFloat': {
    '0%, 100%': { transform: 'translateY(0)' },
    '50%': { transform: 'translateY(-6px)' }
  }
} as const;

export const SubscriptionNotification: React.FC<
  SubscriptionNotificationProps
> = ({ currentSubscription }) => {
  const isCancelled = currentSubscription.endDate !== null;

  if (currentSubscription.package === null) {
    return (
      <Box sx={panelSx}>
        <Box component="span" sx={pillSx(C.dimmer)}>
          <Box
            component="span"
            sx={{ width: 6, height: 6, borderRadius: '50%', background: C.dimmer }}
          />
          No active plan
        </Box>
        <Typography sx={headingSx}>No Subscription</Typography>
        <Typography sx={bodySx}>
          This account has no current subscription — subscribe to unlock bonus
          features and discounts. Check out the packages below.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={panelSx}>
      <Box component="span" sx={pillSx(isCancelled ? C.gold : C.green)}>
        <Box
          component="span"
          sx={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: isCancelled ? C.gold : C.green,
            boxShadow: `0 0 8px ${isCancelled ? C.gold : C.green}`
          }}
        />
        {isCancelled ? 'Cancelled' : 'Active'}
      </Box>
      <Typography sx={eyebrowSx}>Your Membership</Typography>
      <Typography sx={headingSx}>Current Subscription</Typography>

      <Box sx={imgFrameSx}>
        <Box
          component="img"
          src={currentSubscription.package.imageUrl}
          alt="Current subscription"
          sx={{
            display: 'block',
            width: '100%',
            maxWidth: { xs: 320, lg: 420 },
            height: 'auto',
            maxHeight: { xs: 440, lg: 580 },
            objectFit: 'contain',
            filter: 'drop-shadow(0 14px 30px rgba(0, 0, 0, 0.55))'
          }}
        />
      </Box>

      <Typography sx={bodySx}>
        {isCancelled
          ? `Cancelled — your benefits stay active until ${format(
              new Date(currentSubscription.endDate as string),
              'MMMM d, yyyy'
            )}.`
          : 'Renews monthly — your stacking entries go into every $1,000 subscriber giveaway. Manage or change your plan below.'}
      </Typography>
    </Box>
  );
};
